import { contrastRatio, mixColors } from '@avrahami/contracts';
import { makerLine, splitTitle, textColorOn } from '../layout';
import {
  benefitIcon,
  bodyFont,
  cornerOf,
  displayFont,
  drawAmount,
  drawArrow,
  drawIcon,
  drawLines,
  drawLogo,
  drawPhoto,
  drawStruck,
  drawText,
  fillRect,
  fit,
  phoneOf,
  readable,
  strokeRect,
  W,
  WHATSAPP_ICON,
  type Box,
  type Page,
} from '../kit';
import { clipBody, savingOf, tilesOf, WORDS } from './shared';

/**
 * DEPOT: a wholesale depot. Hazard stripes, concrete, black frames with hard offset shadows, the
 * title in tall letters, the photo in a framed box with its loudest figure, a data sheet, a price
 * row of three boxes with the sale in the safety colour, and a black band to call or write.
 */

const CONCRETE = '#ebeae6';
const SHEET = '#f8f8f6';
const RIGHT = 1032;
const LEFT = 48;
const SHADOW = 8;

export function drawDepot(page: Page) {
  const { ctx, flyer } = page;
  const { theme, storeDesign } = flyer;
  const ink = storeDesign.ink;
  // The safety colour: the store's accent, or its brand when the accent is too dark to stand out.
  const safety = contrastRatio(theme.accent, ink) >= 3 ? theme.accent : theme.brand;
  const onSafety = textColorOn(safety);
  const radius = cornerOf(storeDesign.corners, 6);

  /** A box with a black frame and a hard shadow behind it. */
  const block = (box: Box, color: string) => {
    fillRect(ctx, { x: box.x - SHADOW, y: box.y + SHADOW, w: box.w, h: box.h }, ink, radius);
    fillRect(ctx, box, color, radius);
    strokeRect(ctx, box, ink, 3, radius);
  };

  fillRect(ctx, { x: 0, y: 0, w: W, h: W }, CONCRETE);

  // Hazard stripes, and the header.
  fillRect(ctx, { x: 0, y: 0, w: W, h: 18 }, safety);
  ctx.fillStyle = ink;
  for (let x = -18; x < W + 18; x += 40) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + 20, 0);
    ctx.lineTo(x + 2, 18);
    ctx.lineTo(x - 18, 18);
    ctx.fill();
  }
  fillRect(ctx, { x: 0, y: 18, w: W, h: 116 }, SHEET);
  fillRect(ctx, { x: 0, y: 134, w: W, h: 3 }, ink);
  drawLogo(
    page,
    page.images.logo,
    flyer.store.name,
    { x: 560, y: 36, w: RIGHT - 560, h: 82 },
    {
      align: 'right',
      color: ink,
    },
  );
  const saving = savingOf(flyer);
  const tag = saving !== null ? WORDS.sale : flyer.category;
  if (tag !== null) {
    const text = clipBody(page, tag, 800, 21, 360);
    ctx.font = bodyFont(page, 800, 21);
    const width = ctx.measureText(text).width + 40;
    block({ x: LEFT + SHADOW, y: 54, w: width, h: 46 }, safety);
    drawText(ctx, text, LEFT + SHADOW + width / 2, 84, {
      font: bodyFont(page, 800, 21),
      color: onSafety,
      align: 'center',
    });
  }

  // The SKU on a black chip, and the maker's line.
  let x = RIGHT;
  x -= drawText(ctx, WORDS.sku, x, 196, { font: bodyFont(page, 800, 19), color: ink }) + 12;
  ctx.font = bodyFont(page, 800, 19);
  const chip = ctx.measureText(flyer.sku).width + 24;
  fillRect(ctx, { x: x - chip, y: 172, w: chip, h: 34 }, ink);
  drawText(ctx, flyer.sku, x - chip / 2, 196, {
    font: bodyFont(page, 800, 19),
    color: textColorOn(ink),
    align: 'center',
    direction: 'ltr',
  });
  x -= chip + 16;
  const maker = makerLine(flyer.maker);
  if (maker !== '') {
    drawText(ctx, clipBody(page, maker, 700, 19, x - 540), x, 196, {
      font: bodyFont(page, 700, 19),
      color: ink,
      spacing: 2,
    });
  }

  // The data sheet: the specification, or the strengths.
  const rows: { label: string; value: string; icon?: Parameters<typeof benefitIcon>[0] }[] =
    flyer.specs.length > 0
      ? flyer.specs.slice(0, 4)
      : flyer.benefits
          .slice(0, 4)
          .map((benefit) => ({ label: benefit.title, value: '', icon: benefit.icon }));
  const tableBottom = 790;
  const sheetTop = tableBottom - (rows.length === 0 ? 0 : 40 + rows.length * 50);
  if (rows.length > 0) {
    const table = {
      x: 506 + SHADOW,
      y: sheetTop,
      w: RIGHT - 506 - SHADOW,
      h: tableBottom - sheetTop,
    };
    block(table, '#ffffff');
    fillRect(ctx, { x: table.x, y: table.y, w: table.w, h: 40 }, ink, 0);
    drawText(ctx, 'גיליון נתונים', table.x + table.w - 18, table.y + 27, {
      font: bodyFont(page, 800, 17),
      color: textColorOn(ink),
    });
    drawText(ctx, 'DATASHEET', table.x + 18, table.y + 27, {
      font: bodyFont(page, 800, 15),
      color: readable(safety, ink, [textColorOn(ink)]),
      align: 'left',
      direction: 'ltr',
      spacing: 2,
    });
    rows.forEach((row, index) => {
      const top = table.y + 40 + index * 50;
      if (index > 0) fillRect(ctx, { x: table.x, y: top - 1.5, w: table.w, h: 3 }, ink);
      const right = table.x + table.w - 18;
      if (row.icon !== undefined)
        drawIcon(ctx, benefitIcon(row.icon), right - 12, top + 25, 24, ink);
      const valueWidth = row.value === '' ? 0 : table.w * 0.5;
      const labelRight = row.icon === undefined ? right : right - 36;
      drawText(
        ctx,
        clipBody(page, row.label, row.value === '' ? 800 : 600, 20, table.w - 36 - valueWidth),
        labelRight,
        top + 32,
        {
          font: bodyFont(page, row.value === '' ? 800 : 600, 20),
          color: ink,
        },
      );
      if (row.value !== '') {
        drawText(ctx, clipBody(page, row.value, 800, 20, valueWidth - 8), table.x + 18, top + 32, {
          font: bodyFont(page, 800, 20),
          color: ink,
          align: 'left',
        });
      }
    });
  }

  // The title, in tall letters, as large as the room above the data sheet allows.
  const [head, rest] = splitTitle(flyer.title);
  const width = RIGHT - 524;
  const room = (rows.length > 0 ? sheetTop : tableBottom) - 30 - 222;
  const second = readable(theme.brand, CONCRETE, [], 3);
  const marked = contrastRatio(second, ink) < 1.6;
  let head1 = fit(page, head, 'display', {
    maxWidth: width,
    maxLines: 2,
    maxSize: 60,
    minSize: 60,
    weight: 800,
  });
  let rest1 = head1;
  for (let size = 156; size >= 48; size -= 6) {
    head1 = fit(page, head, 'display', {
      maxWidth: width,
      maxLines: 2,
      maxSize: size,
      minSize: size,
      weight: 800,
    });
    rest1 = fit(page, rest === '' ? ' ' : rest, 'display', {
      maxWidth: width - (marked ? 28 : 0),
      maxLines: 2,
      maxSize: Math.round(size * 0.6),
      minSize: Math.round(size * 0.6),
      weight: 800,
    });
    const height =
      head1.lines.length * size * 0.98 +
      (rest === '' ? 0 : rest1.lines.length * rest1.size * 1.1 + 10);
    const cut = [...head1.lines, ...rest1.lines].some((text) => text.endsWith('…'));
    if (height <= room && !cut) break;
  }
  let y = 222;
  y += drawLines(ctx, head1, {
    x: RIGHT,
    y,
    font: displayFont(page, head1.size),
    color: ink,
    lineHeight: 0.98,
  });
  if (rest !== '') {
    y += 10;
    if (marked) {
      // A dark brand on a dark page: the second part on a marker of the safety colour.
      rest1.lines.forEach((text, index) => {
        ctx.font = displayFont(page, rest1.size);
        const w = ctx.measureText(text).width + 28;
        fillRect(
          ctx,
          { x: RIGHT - w, y: y + index * rest1.size * 1.1, w, h: rest1.size * 1.08 },
          safety,
        );
      });
    }
    drawLines(ctx, rest1, {
      x: marked ? RIGHT - 14 : RIGHT,
      y,
      font: displayFont(page, rest1.size),
      color: marked ? onSafety : second,
      lineHeight: 1.1,
    });
  }

  // The photo's box, its tag, and the loudest figure of the product.
  const frame = { x: LEFT + SHADOW, y: 180, w: 440, h: 560 };
  block(frame, '#ffffff');
  const figure = tilesOf(flyer, 1).find((tile) => tile.kind === 'FIGURE');
  drawPhoto(
    page,
    { x: frame.x + 20, y: frame.y + 56, w: frame.w - 40, h: figure === undefined ? 480 : 360 },
    radius,
  );
  const label = flyer.category ?? `${WORDS.sku} ${flyer.sku}`;
  const labelText = clipBody(page, label, 800, 16, 300);
  ctx.font = bodyFont(page, 800, 16);
  const labelWidth = ctx.measureText(labelText).width + 36;
  fillRect(ctx, { x: frame.x, y: frame.y, w: labelWidth, h: 40 }, ink);
  drawText(ctx, labelText, frame.x + labelWidth / 2, frame.y + 27, {
    font: bodyFont(page, 800, 16),
    color: readable(safety, ink, [textColorOn(ink)]),
    align: 'center',
    spacing: 1.5,
  });
  if (figure !== undefined) {
    const right = frame.x + frame.w - 22;
    drawText(
      ctx,
      clipBody(page, figure.figure.label, 700, 17, 300),
      right,
      frame.y + frame.h - 118,
      {
        font: bodyFont(page, 700, 17),
        color: ink,
      },
    );
    const unit = figure.figure.unit;
    const unitWidth =
      unit === ''
        ? 0
        : drawText(ctx, unit, right, frame.y + frame.h - 70, {
            font: displayFont(page, 40),
            color: readable(theme.brand, '#ffffff', [ink]),
            direction: 'ltr',
          });
    const big = fit(page, figure.figure.value, 'display', {
      maxWidth: frame.w - 60 - unitWidth,
      maxLines: 1,
      maxSize: 116,
      minSize: 50,
      weight: 800,
    });
    drawText(
      ctx,
      big.lines[0] ?? '',
      right - unitWidth - (unit === '' ? 0 : 4),
      frame.y + frame.h - 22,
      {
        font: displayFont(page, big.size),
        color: ink,
        direction: 'ltr',
      },
    );
  }

  // The price row: the regular price, the sale in the safety colour, the saving.
  const row = { y: 834, h: 148 };
  const { price } = flyer;
  const labelFont = bodyFont(page, 800, 18);
  if (price !== null && saving !== null && price.compareAtMinor !== null) {
    fillRect(ctx, { x: LEFT, y: row.y + SHADOW, w: RIGHT - LEFT - SHADOW, h: row.h }, ink, radius);
    const parts = [
      { x: RIGHT - 286, w: 286, color: '#ffffff' },
      { x: 362 + SHADOW, w: RIGHT - 286 - 362 - SHADOW, color: safety },
      { x: LEFT + SHADOW, w: 362 - LEFT, color: '#ffffff' },
    ];
    for (const part of parts) {
      fillRect(ctx, { x: part.x, y: row.y, w: part.w, h: row.h }, part.color, radius);
      strokeRect(ctx, { x: part.x, y: row.y, w: part.w, h: row.h }, ink, 3, radius);
    }
    const [regular, sale, saved] = parts as [
      (typeof parts)[0],
      (typeof parts)[0],
      (typeof parts)[0],
    ];
    drawText(ctx, WORDS.regular, regular.x + regular.w / 2, row.y + 46, {
      font: labelFont,
      color: ink,
      align: 'center',
    });
    drawStruck(page, price.compareAtMinor, {
      x: regular.x + regular.w / 2,
      baseline: row.y + 116,
      font: displayFont(page, 50),
      color: ink,
      strike: readable(theme.brand, '#ffffff', [ink]),
      align: 'center',
    });
    drawText(ctx, WORDS.salePrice, sale.x + sale.w / 2, row.y + 40, {
      font: labelFont,
      color: onSafety,
      align: 'center',
    });
    drawAmount(page, price.amountMinor, {
      x: sale.x + sale.w / 2,
      baseline: row.y + 130,
      size: 104,
      color: onSafety,
      align: 'center',
      maxWidth: sale.w - 40,
    });
    drawText(ctx, WORDS.saving, saved.x + saved.w / 2, row.y + 46, {
      font: labelFont,
      color: ink,
      align: 'center',
    });
    drawAmount(page, saving, {
      x: saved.x + saved.w / 2,
      baseline: row.y + 120,
      size: 64,
      color: ink,
      align: 'center',
      maxWidth: saved.w - 40,
    });
  } else if (price !== null) {
    const box = { x: LEFT + SHADOW, y: row.y, w: RIGHT - LEFT - SHADOW, h: row.h };
    block(box, safety);
    drawText(ctx, WORDS.price, box.x + box.w / 2, row.y + 40, {
      font: labelFont,
      color: onSafety,
      align: 'center',
    });
    drawAmount(page, price.amountMinor, {
      x: box.x + box.w / 2,
      baseline: row.y + 130,
      size: 104,
      color: onSafety,
      align: 'center',
      maxWidth: box.w - 60,
    });
  } else {
    // No price: the strengths in the same three boxes (unless the data sheet shows them), or the
    // quote alone.
    const benefits = flyer.specs.length > 0 ? flyer.benefits.slice(0, 3) : [];
    if (benefits.length === 0) {
      const box = { x: LEFT + SHADOW, y: row.y, w: RIGHT - LEFT - SHADOW, h: row.h };
      block(box, safety);
      drawText(ctx, WORDS.quote, box.x + box.w / 2, row.y + 92, {
        font: displayFont(page, 64),
        color: onSafety,
        align: 'center',
        maxWidth: box.w - 60,
      });
    } else {
      const gap = 20;
      const each = (RIGHT - LEFT - SHADOW - gap * (benefits.length - 1)) / benefits.length;
      benefits.forEach((benefit, index) => {
        const box = { x: RIGHT - (index + 1) * each - index * gap, y: row.y, w: each, h: row.h };
        block(box, index === 0 ? safety : '#ffffff');
        const color = index === 0 ? onSafety : ink;
        drawIcon(ctx, benefitIcon(benefit.icon), box.x + box.w - 44, row.y + 44, 36, color, 2.4);
        const text = fit(page, benefit.title, 'body', {
          maxWidth: box.w - 40,
          maxLines: 2,
          maxSize: 24,
          minSize: 18,
          weight: 800,
        });
        drawLines(ctx, text, {
          x: box.x + box.w - 20,
          y: row.y + 74,
          font: bodyFont(page, 800, text.size),
          color,
        });
      });
    }
  }

  // The black band: the quote, the phone, and WhatsApp.
  fillRect(ctx, { x: 0, y: 1012, w: W, h: W - 1012 }, ink);
  const onInk = textColorOn(ink);
  const loud = readable(safety, ink, [onInk]);
  let bandX = RIGHT;
  bandX -=
    drawText(ctx, WORDS.cta, bandX, 1060, { font: displayFont(page, 40), color: onInk }) + 26;
  const phone = phoneOf(flyer);
  if (phone !== null) {
    drawText(ctx, clipBody(page, phone, 800, 30, bandX - 300), bandX, 1058, {
      font: displayFont(page, 40),
      color: loud,
      direction: 'ltr',
    });
  }
  if (flyer.store.whatsapp !== null) {
    const button = { x: LEFT + SHADOW, y: 1022, w: 196, h: 48 };
    fillRect(ctx, button, safety, radius);
    drawIcon(ctx, WHATSAPP_ICON, button.x + button.w - 30, button.y + 24, 26, onSafety, 2.2);
    drawText(ctx, 'וואטסאפ', button.x + button.w - 52, button.y + 33, {
      font: bodyFont(page, 800, 21),
      color: onSafety,
    });
    drawArrow(ctx, button.x + 26, button.y + 24, 22, onSafety, 3);
  }
  // A dark brand looks like ink here: the band's top edge keeps the safety colour.
  fillRect(ctx, { x: 0, y: 1012, w: W, h: 3 }, mixColors(safety, ink, 0.2));
}
